// Phase 14e-a — shared resource panel for all 5 supersedable resources
// (rate-card / rate-modifier / facility-bill-rate-override /
// cancellation-policy / caregiver-guarantee-policy). One parameterized
// component instead of 5 sibling panels — the structural symmetry of
// the backend CRUD shape (verified at Phase 14e-1 coverage sweep) is
// enforced in code so drift between resources is structurally impossible.
//
// Each callsite passes ~30 LOC of config (columns + form fields + endpoint
// callbacks + display labels); the panel handles all the orchestration:
// list query, 3-section partition by status, create/supersede/deprecate
// modals, lead-time gate, localStorage section persistence.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, type FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type { ZodType } from 'zod';
import { Alert, Button, Card, CardHeader, DateInput, Field, Input, useFocusTrap } from '@truecare/ui';
import { actionErrorMessage } from '../lib/action-error';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import {
  LEAD_TIME_DAYS,
  addDays,
  deriveEntryStatus,
  isLeadTimeViolation,
  toIsoDate,
} from '../lib/supersedable-status';

// ─── Base constraint on TItem ───────────────────────────────────────────────

/**
 * Every item the panel manages must expose this minimum shape — the
 * 5 supersedable resource response types all satisfy it.
 */
export interface SupersedableEntry {
  id: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  /** v0/rate-provenance — has this row priced a shift? Drives the per-row
   *  action split: unused → Edit/Delete, used → Supersede/Deprecate. */
  inUse: boolean;
}

// ─── Field descriptor union ─────────────────────────────────────────────────

/**
 * Drives modal form rendering. Callsites declare the resource-specific
 * fields; the panel automatically appends the managed fields
 * (effectiveFrom + reason + overrideLeadTime + overrideReason) so all
 * 5 callsites share identical form chrome around their varying fields.
 */
export type FieldDescriptor =
  | { kind: 'text'; name: string; label: string; placeholder?: string; help?: string }
  | { kind: 'number'; name: string; label: string; min?: number; max?: number; step?: number; help?: string }
  | { kind: 'money'; name: string; label: string; help?: string }
  | { kind: 'percent'; name: string; label: string; help?: string }
  | { kind: 'select'; name: string; label: string; options: readonly { value: string; label: string }[]; help?: string }
  // Free-text input with suggested values (native <datalist>): the operator can
  // pick a suggestion or type a custom value.
  | { kind: 'datalist'; name: string; label: string; options: readonly string[]; placeholder?: string; help?: string };

// ─── Endpoints ──────────────────────────────────────────────────────────────

export interface ResourceEndpoints<TItem extends SupersedableEntry> {
  list: () => Promise<{ items: TItem[] }>;
  create: (body: Record<string, unknown>) => Promise<unknown>;
  supersede: (id: string, body: Record<string, unknown>) => Promise<unknown>;
  deprecate: (id: string, body: Record<string, unknown>) => Promise<unknown>;
  /** Optional (v0/rate-provenance). When BOTH are provided, rows with
   *  inUse=false show Edit + Delete instead of Supersede + Deprecate — an
   *  unused row has priced nothing, so it's safe to mutate/remove in place.
   *  Used rows always keep Supersede + Deprecate. */
  edit?: (id: string, body: Record<string, unknown>) => Promise<unknown>;
  delete?: (id: string) => Promise<unknown>;
}

// ─── Form configuration ─────────────────────────────────────────────────────

export interface ResourceFormConfig<TItem extends SupersedableEntry> {
  /** Form-shape schema (flat — do NOT use API intersection schemas).
   *  Must include managed fields effectiveFrom + reason + override
   *  fields; the panel renders them but the schema validates them. */
  createSchema: ZodType;
  createFields: FieldDescriptor[];

  supersedeSchema: ZodType;
  supersedeFields: FieldDescriptor[];
  /** Maps existing entry → supersede-modal initial values for the
   *  resource-specific fields. effectiveFrom/reason/override are
   *  always reset by the panel (NOT carried forward). */
  supersedeDefaultValues: (
    entry: TItem,
  ) => Record<string, string | number | boolean | null | undefined>;

  /** Deprecate body: { effectiveTo, reason, overrideLeadTime?,
   *  overrideReason? }. Layout is hardcoded; no field descriptors. */
  deprecateSchema: ZodType;

  /** Edit modal (unused rows), required only if endpoints.edit is provided.
   *  VALUE fields only — the edit touches no effective dates, so there's NO
   *  effectiveFrom / reason / lead-time-override section (unlike supersede).
   *  Prefilled from the row via editDefaultValues. */
  editSchema?: ZodType;
  editFields?: FieldDescriptor[];
  editDefaultValues?: (
    entry: TItem,
  ) => Record<string, string | number | boolean | null | undefined>;
}

// ─── Query configuration ────────────────────────────────────────────────────

export interface ResourceQueryConfig {
  /** Unique React Query key for the list query. Used in
   *  invalidateQueries after every mutation. */
  queryKey: readonly string[];
}

// ─── Display strings ────────────────────────────────────────────────────────

export interface PanelLabels {
  panelTitle: string;
  singular: string;
  plural: string;
}

// ─── Main props ─────────────────────────────────────────────────────────────

export interface SupersedableResourcePanelProps<TItem extends SupersedableEntry> {
  labels: PanelLabels;
  /** Columns for AdminTable — DO NOT include an Actions column; the
   *  panel appends Supersede + Deprecate per row automatically. */
  columns: AdminTableColumn<TItem>[];
  endpoints: ResourceEndpoints<TItem>;
  form: ResourceFormConfig<TItem>;
  query: ResourceQueryConfig;
}

// ─── Modal state ────────────────────────────────────────────────────────────

type ModalState<TItem> =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'supersede'; entry: TItem }
  | { mode: 'deprecate'; entry: TItem }
  | { mode: 'edit'; entry: TItem }
  | { mode: 'delete'; entry: TItem };

// ─── localStorage section state ─────────────────────────────────────────────

const LS_KEY_PREFIX = 'supersedable-panel-section-state';

function sectionLsKey(panelTitle: string, section: 'future-effective' | 'deprecated'): string {
  const safe = panelTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return `${LS_KEY_PREFIX}:${safe}:${section}`;
}

function readSectionState(
  panelTitle: string,
  section: 'future-effective' | 'deprecated',
): boolean {
  try {
    return localStorage.getItem(sectionLsKey(panelTitle, section)) === 'true';
  } catch {
    return false;
  }
}

function writeSectionState(
  panelTitle: string,
  section: 'future-effective' | 'deprecated',
  open: boolean,
): void {
  try {
    localStorage.setItem(sectionLsKey(panelTitle, section), String(open));
  } catch {
    /* private-browsing-mode tolerant */
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export function SupersedableResourcePanel<TItem extends SupersedableEntry>(
  props: SupersedableResourcePanelProps<TItem>,
): ReactNode {
  const qc = useQueryClient();
  const LIST_KEY = useMemo(() => [...props.query.queryKey] as const, [props.query.queryKey]);

  const listQuery = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => props.endpoints.list(),
    placeholderData: keepPreviousData,
  });

  const [modal, setModal] = useState<ModalState<TItem>>({ mode: 'closed' });
  const [futureOpen, setFutureOpen] = useState<boolean>(() =>
    readSectionState(props.labels.panelTitle, 'future-effective'),
  );
  const [deprecatedOpen, setDeprecatedOpen] = useState<boolean>(() =>
    readSectionState(props.labels.panelTitle, 'deprecated'),
  );

  // useMemo on [] — stable per render cycle. Section assignment computed
  // at mount time; doesn't shift in real-time. Page refresh picks up the
  // new "now" if the user leaves the panel open across a day boundary.
  const now = useMemo(() => new Date(), []);

  const allItems = listQuery.data?.items ?? [];
  const activeItems = allItems.filter((e) => deriveEntryStatus(e, now) === 'active');
  const futureItems = allItems.filter((e) => deriveEntryStatus(e, now) === 'future-effective');
  const deprecatedItems = allItems.filter((e) => deriveEntryStatus(e, now) === 'deprecated');

  function closeModal(): void {
    setModal({ mode: 'closed' });
  }

  function toggleFuture(): void {
    const next = !futureOpen;
    setFutureOpen(next);
    writeSectionState(props.labels.panelTitle, 'future-effective', next);
  }

  function toggleDeprecated(): void {
    const next = !deprecatedOpen;
    setDeprecatedOpen(next);
    writeSectionState(props.labels.panelTitle, 'deprecated', next);
  }

  function onMutationSuccess(): void {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    closeModal();
  }

  // Edit/Delete are available only when the resource fully opts in (both edit +
  // delete endpoints wired AND the edit form config supplied) AND the specific
  // row is unused. Requiring editSchema + editFields here — not just the
  // endpoints — closes a latent footgun: a consumer that wired the endpoints but
  // forgot editSchema would otherwise render an Edit modal that validates
  // against the supersede schema (whose effectiveFrom/reason inputs are hidden
  // in edit mode) → Save silently fails with no visible error. Missing the form
  // config degrades safely to Supersede/Deprecate instead.
  const editDeleteEnabled = Boolean(
    props.endpoints.edit &&
      props.endpoints.delete &&
      props.form.editSchema &&
      props.form.editFields,
  );

  // Augment row columns with per-row action cell. Unused rows (priced nothing)
  // get Edit + Delete; used rows get Supersede + Deprecate.
  const augmentedColumns: AdminTableColumn<TItem>[] = [
    ...props.columns,
    {
      key: '__actions',
      header: 'Actions',
      sortable: false,
      width: 'w-44',
      render: (row) =>
        editDeleteEnabled && !row.inUse ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setModal({ mode: 'edit', entry: row })}
              data-testid={`edit-${row.id}`}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setModal({ mode: 'delete', entry: row })}
              data-testid={`delete-${row.id}`}
            >
              Delete
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setModal({ mode: 'supersede', entry: row })}
              data-testid={`supersede-${row.id}`}
            >
              Supersede
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setModal({ mode: 'deprecate', entry: row })}
              data-testid={`deprecate-${row.id}`}
            >
              Deprecate
            </Button>
          </div>
        ),
    },
  ];

  if (listQuery.isError) {
    return (
      <Card padding="lg">
        <CardHeader title={props.labels.panelTitle} />
        <Alert tone="danger" className="mt-4">
          Failed to load {props.labels.plural}. Check your connection and refresh
          the page.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => listQuery.refetch()}
            data-testid="supersedable-retry"
          >
            Retry
          </button>
        </Alert>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between">
        <CardHeader title={props.labels.panelTitle} />
        <Button
          variant="primary"
          onClick={() => setModal({ mode: 'create' })}
          data-testid="supersedable-create-button"
        >
          Create {props.labels.singular}
        </Button>
      </div>

      {/* Active section — always expanded */}
      <section className="mt-6">
        <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
          Active
        </h3>
        <AdminTable
          columns={augmentedColumns}
          rows={activeItems}
          isLoading={listQuery.isPending}
          page={1}
          limit={activeItems.length || 1}
          totalCount={activeItems.length}
          onPageChange={() => undefined}
          emptyMessage={`No active ${props.labels.plural}.`}
        />
      </section>

      {/* Future-effective section — collapsed by default */}
      <section className="mt-6">
        <button
          type="button"
          onClick={toggleFuture}
          aria-expanded={futureOpen}
          aria-controls="supersedable-future-content"
          className="font-heading text-sm uppercase tracking-wide text-forest mb-2 hover:text-gold"
          data-testid="supersedable-toggle-future"
        >
          {futureOpen ? '▼' : '▶'} Future-effective ({futureItems.length})
        </button>
        {futureOpen && (
          <div id="supersedable-future-content">
            <AdminTable
              columns={augmentedColumns}
              rows={futureItems}
              page={1}
              limit={futureItems.length || 1}
              totalCount={futureItems.length}
              onPageChange={() => undefined}
              emptyMessage={`No future-effective ${props.labels.plural}. Create one to schedule a change.`}
            />
          </div>
        )}
      </section>

      {/* Deprecated section — collapsed by default */}
      <section className="mt-6">
        <button
          type="button"
          onClick={toggleDeprecated}
          aria-expanded={deprecatedOpen}
          aria-controls="supersedable-deprecated-content"
          className="font-heading text-sm uppercase tracking-wide text-forest mb-2 hover:text-gold"
          data-testid="supersedable-toggle-deprecated"
        >
          {deprecatedOpen ? '▼' : '▶'} Deprecated ({deprecatedItems.length})
        </button>
        {deprecatedOpen && (
          <div id="supersedable-deprecated-content">
            <AdminTable
              columns={augmentedColumns}
              rows={deprecatedItems}
              page={1}
              limit={deprecatedItems.length || 1}
              totalCount={deprecatedItems.length}
              onPageChange={() => undefined}
              emptyMessage={`No deprecated ${props.labels.plural}.`}
            />
          </div>
        )}
      </section>

      {modal.mode !== 'closed' && (
        <ResourceModal
          modal={modal}
          labels={props.labels}
          form={props.form}
          endpoints={props.endpoints}
          onSuccess={onMutationSuccess}
          onClose={closeModal}
        />
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal sub-component — handles all three modes (create / supersede / deprecate)
// ────────────────────────────────────────────────────────────────────────────

interface ResourceModalProps<TItem extends SupersedableEntry> {
  modal: Exclude<ModalState<TItem>, { mode: 'closed' }>;
  labels: PanelLabels;
  form: ResourceFormConfig<TItem>;
  endpoints: ResourceEndpoints<TItem>;
  onSuccess: () => void;
  onClose: () => void;
}

function ResourceModal<TItem extends SupersedableEntry>(
  props: ResourceModalProps<TItem>,
): ReactNode {
  const isDeprecate = props.modal.mode === 'deprecate';
  const isEdit = props.modal.mode === 'edit';
  const isDelete = props.modal.mode === 'delete';
  const schema =
    props.modal.mode === 'create'
      ? props.form.createSchema
      : props.modal.mode === 'supersede'
        ? props.form.supersedeSchema
        : isEdit
          ? (props.form.editSchema ?? props.form.supersedeSchema)
          : // deprecate + delete (delete never renders/validates the form)
            props.form.deprecateSchema;
  const fields =
    props.modal.mode === 'create'
      ? props.form.createFields
      : props.modal.mode === 'supersede'
        ? props.form.supersedeFields
        : isEdit
          ? (props.form.editFields ?? [])
          : [];

  const now = useMemo(() => new Date(), []);
  const defaultEffectiveFrom = toIsoDate(addDays(now, LEAD_TIME_DAYS));

  // Per-mode default values.
  const initialDefaults = useMemo<Record<string, unknown>>(() => {
    if (props.modal.mode === 'create') {
      return {
        effectiveFrom: defaultEffectiveFrom,
        reason: '',
        overrideLeadTime: false,
        overrideReason: '',
      };
    }
    if (props.modal.mode === 'supersede') {
      return {
        ...props.form.supersedeDefaultValues(props.modal.entry),
        effectiveFrom: defaultEffectiveFrom,
        reason: '',
        overrideLeadTime: false,
        overrideReason: '',
      };
    }
    if (props.modal.mode === 'edit') {
      // Value fields only — prefilled from the row. No effectiveFrom / reason /
      // override (edit touches no dates).
      return { ...(props.form.editDefaultValues?.(props.modal.entry) ?? {}) };
    }
    if (props.modal.mode === 'delete') {
      return {}; // confirmation-only; no form fields
    }
    // deprecate
    const future = deriveEntryStatus(props.modal.entry, now) === 'future-effective';
    return {
      // For a future-effective entry, default effectiveTo to its
      // effectiveFrom (operator most likely intent: cancel before it
      // activates). For active entries, default to today.
      effectiveTo: future ? props.modal.entry.effectiveFrom : toIsoDate(now),
      reason: '',
      overrideLeadTime: false,
      overrideReason: '',
    };
  }, [props.modal, props.form, defaultEffectiveFrom, now]);

  const reactForm = useForm<FieldValues>({
    resolver: zodResolver(schema),
    defaultValues: initialDefaults,
    mode: 'onSubmit',
  });

  // Destructure isDirty here so RHF's formState Proxy subscribes to
  // updates — accessing formState.isDirty later inside callbacks
  // captures a stale render-time value (the Proxy only tracks fields
  // observed during render).
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { isDirty, errors },
    reset,
  } = reactForm;

  // Re-seed form when the modal entry changes (operator opens supersede
  // on a different row).
  useEffect(() => {
    reset(initialDefaults);
  }, [initialDefaults, reset]);

  const mutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (props.modal.mode === 'create') return props.endpoints.create(body);
      if (props.modal.mode === 'supersede')
        return props.endpoints.supersede(props.modal.entry.id, body);
      if (props.modal.mode === 'edit' && props.endpoints.edit)
        return props.endpoints.edit(props.modal.entry.id, body);
      if (props.modal.mode === 'delete' && props.endpoints.delete)
        return props.endpoints.delete(props.modal.entry.id);
      return props.endpoints.deprecate(props.modal.entry.id, body);
    },
    onSuccess: () => {
      props.onSuccess();
      reset();
    },
  });

  const watchedEffectiveFrom = watch('effectiveFrom') as string | undefined;
  const watchedEffectiveTo = watch('effectiveTo') as string | undefined;
  const watchedOverride = watch('overrideLeadTime') as boolean | undefined;

  // Lead-time gate. For create + supersede, gate on effectiveFrom. For
  // deprecate, gate on effectiveTo (urgent deprecation = within lead
  // time = needs override + reason).
  const dateToGate = isDeprecate ? watchedEffectiveTo : watchedEffectiveFrom;
  const leadTimeWarning =
    dateToGate !== undefined &&
    isLeadTimeViolation(dateToGate, now, Boolean(watchedOverride));

  // Auto-open the override section the moment the chosen date falls inside the
  // 14-day window — that's precisely when the operator needs it. Leaving it
  // collapsed there was the main "why won't this submit?" confusion. Tracked as
  // state (not a hardcoded `open={leadTimeWarning}`) so the operator can still
  // collapse it, and onToggle keeps our copy in sync with manual changes.
  const [overrideOpen, setOverrideOpen] = useState(false);
  useEffect(() => {
    if (leadTimeWarning) setOverrideOpen(true);
  }, [leadTimeWarning]);

  function onCancel(): void {
    if (isDirty) {
       
      const confirmed = window.confirm('Discard unsaved changes?');
      if (!confirmed) return;
    }
    reset();
    props.onClose();
  }

  const title =
    props.modal.mode === 'create'
      ? `Create ${props.labels.singular}`
      : props.modal.mode === 'supersede'
        ? `Supersede ${props.labels.singular}`
        : isEdit
          ? `Edit ${props.labels.singular}`
          : isDelete
            ? `Delete ${props.labels.singular}`
            : `Deprecate ${props.labels.singular}`;
  const titleId = `supersedable-modal-title-${props.modal.mode}`;

  // Focus in, trap Tab, Escape-to-close (respecting the dirty-form
  // confirm inside onCancel), restore focus on unmount (H-F4). Mirrors
  // the modal-dismissal expectation operators have from TotpReEnrollModal.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { onEscape: () => onCancel() });

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid={`supersedable-${props.modal.mode}-modal`}
    >
      <div className="w-full max-w-2xl rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <h2 id={titleId} className="font-heading text-xl text-forest">{title}</h2>
        {mutation.isError && actionErrorMessage(mutation.error) && (
          <Alert tone="danger" className="mt-4">
            {actionErrorMessage(mutation.error)}
          </Alert>
        )}
        {isDelete ? (
          <div className="mt-4 flex flex-col gap-4">
            <Alert tone="warning">
              This permanently deletes a {props.labels.singular} that has priced
              nothing. This can&apos;t be undone. (A {props.labels.singular} that
              has been used shows Supersede/Deprecate instead — never Delete.)
            </Alert>
            <div className="mt-2 flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                loading={mutation.isPending}
                onClick={() => mutation.mutate({})}
                data-testid="supersedable-delete-submit"
              >
                Delete
              </Button>
            </div>
          </div>
        ) : (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={handleSubmit((data) => {
            // The lead-time override pair is OPTIONAL on the wire. RHF seeds
            // the form with defaults (overrideLeadTime:false, overrideReason:'')
            // so the controlled inputs stay defined — but an empty
            // overrideReason trips the schema's z.string().min(10) even when
            // the operator never touched the override. Omit the pair entirely
            // unless the override is actually enabled.
            const body: Record<string, unknown> = { ...data };
            if (!body['overrideLeadTime']) {
              delete body['overrideLeadTime'];
              delete body['overrideReason'];
            }
            mutation.mutate(body);
          })}
          noValidate
        >
          {/* Resource-specific fields */}
          {fields.map((descriptor) => (
            <DescriptorField
              key={descriptor.name}
              descriptor={descriptor}
              register={register}
              error={
                ((errors[descriptor.name] as { message?: string } | undefined)?.message) ?? null
              }
            />
          ))}

          {/* Effective-from for create / supersede; effective-to for deprecate.
              Edit touches no dates (value fields only). */}
          {!isDeprecate && !isEdit && (
            <Field
              label="Effective from"
              error={
                ((errors["effectiveFrom"] as { message?: string } | undefined)?.message) ?? null
              }
            >
              <Controller
                name="effectiveFrom"
                control={control}
                render={({ field }) => (
                  <DateInput
                    name={field.name}
                    value={(field.value as string | undefined) ?? ''}
                    onChange={field.onChange}
                    data-testid="supersedable-effective-from"
                  />
                )}
              />
            </Field>
          )}
          {isDeprecate && (
            <Field
              label="Effective to"
              error={
                ((errors["effectiveTo"] as { message?: string } | undefined)?.message) ?? null
              }
            >
              <Controller
                name="effectiveTo"
                control={control}
                render={({ field }) => (
                  <DateInput
                    name={field.name}
                    value={(field.value as string | undefined) ?? ''}
                    onChange={field.onChange}
                    data-testid="supersedable-effective-to"
                  />
                )}
              />
            </Field>
          )}

          {leadTimeWarning && (
            <Alert tone="warning">
              This date is within {LEAD_TIME_DAYS} days of today. To proceed,
              complete the override section below (expanded automatically):
              provide a reason and check the confirmation.
            </Alert>
          )}

          {/* Edit (unused rows) is not versioned → no reason field. */}
          {!isEdit && (
            <Field
              label="Reason (≥10 characters)"
              error={
                ((errors["reason"] as { message?: string } | undefined)?.message) ?? null
              }
            >
              <textarea
                rows={3}
                {...register('reason')}
                className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                data-testid="supersedable-reason"
              />
            </Field>
          )}

          {/* Edit touches no dates → no lead-time override section. */}
          {!isEdit && (
          <details
            className="rounded-md border border-border p-3"
            open={overrideOpen}
            onToggle={(e) => setOverrideOpen((e.currentTarget as HTMLDetailsElement).open)}
            data-testid="supersedable-override-section"
          >
            <summary className="cursor-pointer text-sm font-heading text-forest">
              Enable override of the {LEAD_TIME_DAYS}-day lead time requirement
            </summary>
            <Alert tone="warning" className="mt-3">
              This only <strong>allows</strong> an effective date inside the{' '}
              {LEAD_TIME_DAYS}-day window — it doesn&apos;t set one for you. Set
              the &ldquo;{isDeprecate ? 'Effective to' : 'Effective from'}&rdquo;
              date above to when the change should actually take effect (it
              defaults to {LEAD_TIME_DAYS} days out), then provide a reason below
              and check the confirmation. The override is logged permanently.
            </Alert>
            <Field
              label="Override reason (≥10 characters)"
              error={
                ((errors["overrideReason"] as { message?: string } | undefined)?.message) ?? null
              }
              className="mt-3"
            >
              <textarea
                rows={2}
                {...register('overrideReason')}
                className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                data-testid="supersedable-override-reason"
              />
            </Field>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-forest"
                {...register('overrideLeadTime')}
                data-testid="supersedable-override-checkbox"
              />
              I confirm this override is necessary
            </label>
          </details>
          )}

          <div className="mt-2 flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
              disabled={leadTimeWarning}
              data-testid="supersedable-submit"
            >
              {props.modal.mode === 'create'
                ? 'Create'
                : props.modal.mode === 'supersede'
                  ? 'Supersede'
                  : isEdit
                    ? 'Save changes'
                    : 'Deprecate'}
            </Button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Field renderer — maps FieldDescriptor.kind → form input.
// ────────────────────────────────────────────────────────────────────────────

interface DescriptorFieldProps {
  descriptor: FieldDescriptor;
  register: ReturnType<typeof useForm>['register'];
  error: string | null;
}

function DescriptorField({ descriptor, register, error }: DescriptorFieldProps): ReactNode {
  // @truecare/ui Field clones its single child to inject id/aria attrs,
  // so we pass exactly ONE form element as the child + render the
  // currency/percent decoration inside the input's label instead of
  // alongside the input. Hint goes via Field's hint prop.
  const labelWithDecoration =
    descriptor.kind === 'money'
      ? `${descriptor.label} ($)`
      : descriptor.kind === 'percent'
        ? `${descriptor.label} (%)`
        : descriptor.label;

  if (descriptor.kind === 'select') {
    return (
      <Field label={labelWithDecoration} error={error} hint={descriptor.help}>
        <select
          {...register(descriptor.name)}
          className="w-full rounded-md border border-border bg-cream p-2 text-sm"
          data-testid={`supersedable-field-${descriptor.name}`}
        >
          <option value="">Select…</option>
          {descriptor.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (descriptor.kind === 'datalist') {
    const listId = `datalist-${descriptor.name}`;
    return (
      <>
        <Field label={descriptor.label} error={error} hint={descriptor.help}>
          <Input
            type="text"
            list={listId}
            placeholder={descriptor.placeholder}
            {...register(descriptor.name)}
            data-testid={`supersedable-field-${descriptor.name}`}
          />
        </Field>
        <datalist id={listId}>
          {descriptor.options.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      </>
    );
  }

  if (descriptor.kind === 'number') {
    return (
      <Field label={labelWithDecoration} error={error} hint={descriptor.help}>
        <Input
          type="number"
          min={descriptor.min}
          max={descriptor.max}
          step={descriptor.step}
          {...register(descriptor.name, { valueAsNumber: true })}
          data-testid={`supersedable-field-${descriptor.name}`}
        />
      </Field>
    );
  }

  // text / money / percent — all string inputs
  return (
    <Field label={labelWithDecoration} error={error} hint={descriptor.help}>
      <Input
        type="text"
        inputMode={descriptor.kind === 'text' ? undefined : 'decimal'}
        placeholder={descriptor.kind === 'text' ? descriptor.placeholder : undefined}
        {...register(descriptor.name)}
        data-testid={`supersedable-field-${descriptor.name}`}
      />
    </Field>
  );
}
